import type React from 'react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';

type LastTransaction = {
  amount?: number;
  qr_code?: string;
  url?: string;
  pdf?: string;
  line?: string;
  barcode?: string;
  due_at?: string;
};
type Charge = {
  payment_method: 'pix' | 'boleto' | string;
  last_transaction: LastTransaction;
  customer?: { name?: string; email?: string };
};
type PaymentData = {
  id: string;
  paymentMethod: string;
  totalValue: number; // reais
  charges: Charge[];
  customer?: { name: string; email: string };
};

function normalizePayment(raw: any): PaymentData {
  const ch0 = raw?.charges?.[0] ?? {};
  return {
    id: String(raw?.id ?? ''),
    paymentMethod: String(ch0?.payment_method ?? ''),
    totalValue: Number(raw?.totalValue ?? 0),
    charges: Array.isArray(raw?.charges) ? raw.charges : [],
    customer: ch0?.customer
      ? {
          name: String(ch0.customer.name ?? ''),
          email: String(ch0.customer.email ?? ''),
        }
      : undefined,
  };
}

const formatCurrency = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

export const PaymentResultScreen: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [copied, setCopied] = useState(false);

  const isSafeUrl = (u?: string) =>
    typeof u === 'string' && /^https?:\/\//i.test(u);

  useEffect(() => {
    // const statePd = (
    //   location.state as { paymentData?: PaymentData } | undefined
    // )?.paymentData;
    // if (statePd) {
    //   setPaymentData(statePd);
    //   return;
    //}
    const raw = localStorage.getItem('paymentData');
    console.log(raw);
    if (raw) {
      const parsed = JSON.parse(raw);
      setPaymentData(normalizePayment(parsed));
    }
  }, [location.state, history]);

  const copyToClipboard = (text?: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Cálculo sem hooks, tolerante a nulos
  const headerCents = (() => {
    const amount = paymentData?.charges?.[0]?.last_transaction?.amount;
    if (typeof amount === 'number' && amount > 0) return amount;
    const tv = paymentData?.totalValue ?? 0;
    return Math.round(tv * 100);
  })();

  if (
    !paymentData ||
    !paymentData.charges?.length ||
    !paymentData.charges[0]?.last_transaction
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto" />
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  const charge = paymentData.charges[0];
  const transaction = charge.last_transaction;
  const isPix =
    charge.payment_method === 'pix' &&
    typeof transaction.qr_code === 'string' &&
    !!transaction.qr_code;
  const isBoleto = charge.payment_method === 'boleto';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Pedido Confirmado
            </h1>
            <p className="text-gray-600">Pedido #{paymentData.id.slice(-8)}</p>
            <p className="text-xl font-bold text-red-600 mt-2">
              {formatCurrency(headerCents)}
            </p>
          </div>
        </div>

        {isPix && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-600 rounded" />
              Pagamento via PIX
            </h2>

            <div className="text-center mb-6">
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                <QRCodeCanvas
                  value={transaction.qr_code as string}
                  size={200}
                  includeMargin
                />
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Escaneie o QR Code com seu app do banco
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código PIX Copia e Cola
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={transaction.qr_code}
                    readOnly
                    className="flex-1 p-3 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(transaction.qr_code)}
                    className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Como pagar</h3>
                <ol className="text-sm text-blue-800 space-y-1">
                  <li>1. Abra o app do seu banco</li>
                  <li>2. Escaneie o QR Code ou cole o código PIX</li>
                  <li>3. Confirme o pagamento</li>
                  <li>4. O pagamento é processado instantaneamente</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {isBoleto && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-6 h-6 bg-orange-600 rounded" />
              Pagamento via Boleto
            </h2>

            <div className="space-y-4">
              {transaction.due_at && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-orange-800">
                    <strong>Vencimento:</strong>{' '}
                    {formatDate(transaction.due_at)}
                  </p>
                </div>
              )}

              {transaction.line && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Linha Digitável
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={transaction.line}
                      readOnly
                      className="flex-1 p-3 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(transaction.line)}
                      className="px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      {copied ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                </div>
              )}

              {isSafeUrl(transaction.barcode) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código de Barras
                  </label>
                  <div className="p-3 border border-gray-300 rounded-lg bg-white">
                    <img
                      src={transaction.barcode as string}
                      alt="Código de barras do boleto"
                      className="mx-auto max-h-24"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display =
                          'none';
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                {transaction.pdf && (
                  <a
                    href={transaction.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors text-center font-medium"
                  >
                    Baixar PDF
                  </a>
                )}
                {transaction.url && (
                  <a
                    href={transaction.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors text-center font-medium"
                  >
                    Ver Boleto
                  </a>
                )}
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h3 className="font-semibold text-orange-900 mb-2">
                  Como pagar
                </h3>
                <ol className="text-sm text-orange-800 space-y-1">
                  <li>1. Baixe o PDF ou copie a linha digitável</li>
                  <li>2. Pague em qualquer banco, lotérica ou app bancário</li>
                  <li>3. A compensação pode levar até 3 dias úteis</li>
                  <li>4. Após confirmação, o pedido será processado</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => history.push('/user-profile?tab=acompanhar-pedidos')}
            className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Ver Meus Pedidos
          </button>
          <button
            onClick={() => history.push('/')}
            className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Continuar Comprando
          </button>
        </div>
      </div>
    </div>
  );
};
